/**
 * ML model configuration for browser-side inference.
 * Defines model URLs, memory budgets, inference thresholds, and batch sizes
 * for the ONNX Runtime / Transformers.js browser provider.
 */

export type MLBackend = 'webgpu' | 'webgl' | 'wasm' | 'none';

export interface MLModelDef {
  /** Model identifier. */
  id: string;
  /** Human-readable name. */
  name: string;
  /** Task this model performs. */
  task: 'embeddings' | 'classification' | 'zero-shot';
  /** ONNX model URL (CDN-hosted, quantized). */
  modelUrl: string;
  /** Tokenizer URL (JSON). */
  tokenizerUrl: string;
  /** Approximate model size in MB (for memory budget). */
  sizeMb: number;
  /** Maximum input token length. */
  maxTokens: number;
  /** Output embedding dimension (for embedding models). */
  embeddingDim?: number;
}

/** Available models for browser-side inference. */
export const ML_MODELS: Record<string, MLModelDef> = {
  'minilm-embeddings': {
    id: 'minilm-embeddings',
    name: 'MiniLM-L6-v2 (Embeddings)',
    task: 'embeddings',
    modelUrl: 'https://huggingface.co/Xenova/all-MiniLM-L6-v2/resolve/main/onnx/model_quantized.onnx',
    tokenizerUrl: 'https://huggingface.co/Xenova/all-MiniLM-L6-v2/resolve/main/tokenizer.json',
    sizeMb: 23,
    maxTokens: 256,
    embeddingDim: 384,
  },
  'distilbert-classification': {
    id: 'distilbert-classification',
    name: 'DistilBERT (Classification)',
    task: 'classification',
    modelUrl: 'https://huggingface.co/Xenova/distilbert-base-uncased-finetuned-sst-2-english/resolve/main/onnx/model_quantized.onnx',
    tokenizerUrl: 'https://huggingface.co/Xenova/distilbert-base-uncased-finetuned-sst-2-english/resolve/main/tokenizer.json',
    sizeMb: 67,
    maxTokens: 512,
  },
};

/** Memory budgets per backend (in MB). */
export const MEMORY_BUDGETS: Record<MLBackend, number> = {
  webgpu: 512,
  webgl: 256,
  wasm: 128,
  none: 0,
};

/** Inference configuration. */
export const ML_INFERENCE_CONFIG = {
  /** Batch size for embedding requests. */
  embeddingBatchSize: 16,
  /** Batch size for classification requests. */
  classificationBatchSize: 8,
  /** Confidence threshold for classification results. */
  classificationThreshold: 0.6,
  /** Cosine similarity threshold for "related" events in correlation. */
  semanticSimilarityThreshold: 0.65,
  /** Worker message timeout (ms). */
  workerTimeoutMs: 30_000,
  /** Maximum concurrent inference requests. */
  maxConcurrent: 2,
} as const;

/**
 * Detect the best available ML backend for the current browser.
 * Priority: WebGPU > WebGL > WASM > none.
 */
export async function detectMLBackend(): Promise<MLBackend> {
  // WebGPU check
  if (typeof navigator !== 'undefined' && 'gpu' in navigator) {
    try {
      const adapter = await (navigator as unknown as { gpu: { requestAdapter(): Promise<unknown | null> } }).gpu.requestAdapter();
      if (adapter) return 'webgpu';
    } catch {
      // WebGPU not usable
    }
  }

  // WebGL2 check
  if (typeof document !== 'undefined') {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl2');
      if (gl) return 'webgl';
    } catch {
      // WebGL not usable
    }
  }

  // WASM is always available in modern browsers
  if (typeof WebAssembly !== 'undefined') {
    return 'wasm';
  }

  return 'none';
}

/**
 * Check whether the memory budget supports loading a given model
 * on the detected backend.
 */
export function canLoadModel(modelId: string, backend: MLBackend): boolean {
  const model = ML_MODELS[modelId];
  if (!model) return false;
  return model.sizeMb <= MEMORY_BUDGETS[backend];
}
